import {
  Link45Icon,
  NumberOne,
  NumberThree,
  NumberTwo,
  UpArrowWithWhiteBackground,
} from '../../svg';
import { useTranslation } from 'react-i18next';
import Input from '../common/Input';
import { Button } from '../common/Button';
import { UserContext } from '@/context/UserContext';
import { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import UpgradeRequiredModal from '../Modal/UpgradeRequiredModal';
import { useNavigate } from 'react-router-dom';
import { useCreateLinkMutation } from '@/hooks/queries/useLinkQueries';

interface DashboardBlockProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const DashboardBlock: React.FC<DashboardBlockProps> = ({
  icon,
  title,
  description,
}) => {
  return (
    <div className="rounded-3xl bg-[#E1D6FF] p-5">
      <div className="flex flex-col gap-2">
        <div className="flex flex-row-reverse">{icon}</div>
        <div className="text-base font-[800] text-[#140732]">
          <span>{title}</span>
        </div>
        <div className="text-sm text-[#2F1869]">
          <span>{description}</span>
        </div>
      </div>
    </div>
  );
};

const BlankDashboard: React.FC = () => {
  const { t } = useTranslation();
  const userContext = useContext(UserContext);
  const [newLink, setNewLink] = useState('');
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const navigate = useNavigate();

  const dashboardData = {
    title: t('dashboard.emptyDashboard.title'),
    blocks: [
      {
        icon: <NumberOne fill={'#fff'} textColor={'#2F1869'} />,
        title: t('dashboard.emptyDashboard.createSmartLink'),
        description: t('dashboard.emptyDashboard.createSmartLinkDesc'),
      },
      {
        icon: <NumberTwo fill={'#fff'} textColor={'#2F1869'} />,
        title: t('dashboard.emptyDashboard.addAnywhere'),
        description: t('dashboard.emptyDashboard.addAnywhereDesc'),
      },
      {
        icon: <NumberThree fill={'#fff'} textColor={'#2F1869'} />,
        title: t('dashboard.emptyDashboard.goViral'),
        description: t('dashboard.emptyDashboard.goViralDesc'),
      },
    ],
  };

  if (!userContext) {
    throw new Error('useContext must be used within a UserProvider');
  }

  const { user } = userContext;
  const createLinkMutation = useCreateLinkMutation(user?._id || '');

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNewLink(event.target.value);
  };

  const handleCreateLink = async () => {
    const urlRegex = new RegExp(
      '^(http://www.|https://www.|http://|https://)?[a-z0-9]+([\\-\\.]{1}[a-z0-9]+)*\\.[a-z]{2,5}(:[0-9]{1,5})?(\\/.*)?$',
    );
    if (!urlRegex.test(newLink)) {
      toast.error(t('errors.invalidUrl'));
      return;
    }
    if (user && user._id && newLink) {
      try {
        await createLinkMutation.mutateAsync(newLink);
        toast.success(t('dashboard.linkCreatedSuccess'));
        setNewLink('');
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.status === 409) {
            setIsUpgradeModalOpen(true);
            return;
          }
        }
        toast.error(t('errors.failedToCreateLink'));
      }
    }
  };

  const handleUpgradeClick = () => {
    setIsUpgradeModalOpen(false);
    navigate('/plans');
  };

  return (
    <>
      <div className="bg-[#F0F5FF] h-full  lg:pb-[111px]">
        <div className="flex items-center flex-col gap-4 lg:bg-white lg:rounded-3xl lg:p-8 lg:items-start">
          <div className="lg:hidden">
            <UpArrowWithWhiteBackground />
          </div>
          <div className="w-full text-center ">
            <span className="text-2xl text-[#172B4D] lg:text-[32px] lg:leading-[54px] font-[Manrope]">
              {dashboardData.title}
            </span>
          </div>
          {user?.role === 'brandUser' && (
            <div className=" flex flex-col items-center gap-4 w-full">
              <Input
                containerWrapperClass="w-full md:w-[432px] font-[Manrope]"
                placeholder={String(t('dashboard.pasteLinkHere'))}
                icon={<Link45Icon />}
                iconPosition="end"
                onChange={handleInputChange}
                value={newLink}
              />
              <Button
                disabled={createLinkMutation.isPending}
                onClick={handleCreateLink}
                className=" max-md:w-full"
              >
                {createLinkMutation.isPending
                  ? t('dashboard.creating')
                  : t('dashboard.createSmartLink')}
              </Button>
            </div>
          )}
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3 font-[Inter]">
            {dashboardData.blocks.map((block, index) => (
              <DashboardBlock
                key={index}
                icon={block.icon}
                title={block.title}
                description={block.description}
              />
            ))}
          </div>
        </div>
      </div>{' '}
      <UpgradeRequiredModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onUpgrade={handleUpgradeClick}
      />
    </>
  );
};

export default BlankDashboard;
